import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Check, ChevronDown, Minus } from 'lucide-react';
import { textIncludesQuery, textsEqualForSearch } from '@narrativebench/editor-core';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '@/lib/utils';
import {
  cycleFilterOptionPolarity,
  formatIncludeExcludeCount,
  getFilterOptionPolarity,
} from '@/utils/filter-option-polarity';

export interface MultiSelectComboboxOption {
  value: string;
  label: string;
  swatchColor?: string;
}

interface MultiSelectComboboxProps {
  options: MultiSelectComboboxOption[];
  selected: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
  excluded?: Set<string>;
  onExcludedChange?: (excluded: Set<string>) => void;
  allowExclude?: boolean;
  placeholder?: string;
  label?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
  clearAllLabel?: string;
  includeExcludeHint?: string;
  createLabel?: (query: string) => string;
  className?: string;
  align?: 'start' | 'center' | 'end';
  triggerClassName?: string;
}

export const MultiSelectCombobox: React.FC<MultiSelectComboboxProps> = ({
  options,
  selected,
  onSelectionChange,
  excluded,
  onExcludedChange,
  allowExclude = false,
  placeholder,
  label,
  emptyMessage = 'No results found.',
  searchPlaceholder = 'Search...',
  clearAllLabel = 'Clear all',
  includeExcludeHint,
  createLabel,
  className,
  align = 'start',
  triggerClassName,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const activeExcluded = allowExclude ? (excluded ?? new Set<string>()) : new Set<string>();

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    return options.filter(
      (opt) => textIncludesQuery(opt.value, search) || textIncludesQuery(opt.label, search)
    );
  }, [options, search]);

  const normalizedSearch = search.trim().toLowerCase();
  const canCreateOption = Boolean(
    createLabel &&
      normalizedSearch.length > 0 &&
      !options.some(
        (option) =>
          option.value === normalizedSearch || textsEqualForSearch(option.label, search)
      ) &&
      !selected.has(normalizedSearch) &&
      !activeExcluded.has(normalizedSearch)
  );

  const applySelection = (nextIncluded: Set<string>, nextExcluded: Set<string>): void => {
    onSelectionChange(nextIncluded);
    if (allowExclude) {
      onExcludedChange?.(nextExcluded);
    }
  };

  const handleToggle = (value: string): void => {
    if (allowExclude) {
      const next = cycleFilterOptionPolarity(value, [...selected], [...activeExcluded]);
      applySelection(new Set(next.includedIds), new Set(next.excludedIds));
      return;
    }
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onSelectionChange(next);
  };

  const createFromSearch = (): void => {
    if (!canCreateOption) {
      return;
    }
    const nextIncluded = new Set(selected);
    nextIncluded.add(normalizedSearch);
    const nextExcluded = new Set(activeExcluded);
    nextExcluded.delete(normalizedSearch);
    applySelection(nextIncluded, nextExcluded);
    setSearch('');
  };

  const clearAll = (): void => {
    applySelection(new Set(), new Set());
  };

  const displayLabel = label ?? placeholder;
  const countLabel = formatIncludeExcludeCount(selected.size, activeExcluded.size);
  const countSuffix = countLabel ? ` (${countLabel})` : '';
  const closedDisplay = `${displayLabel}${countSuffix}`;
  const hasActiveSelection = selected.size > 0 || activeExcluded.size > 0;

  useEffect(() => {
    if (!open) {
      setSearch('');
    } else {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const displayValue = open ? search : closedDisplay;
  const inputPlaceholder = open ? searchPlaceholder : displayLabel;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            'flex h-9 w-full min-w-[140px] items-center gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus-within:outline-none focus-within:ring-1 focus-within:ring-ring',
            triggerClassName
          )}
        >
          <input
            ref={inputRef}
            value={displayValue}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canCreateOption) {
                e.preventDefault();
                createFromSearch();
              }
            }}
            placeholder={inputPlaceholder}
            readOnly={!open}
            className="flex-1 min-w-0 bg-transparent outline-none placeholder:text-muted-foreground"
          />
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </div>
      </PopoverTrigger>
      <PopoverContent className={cn('w-[var(--radix-popover-trigger-width)] p-0', className)} align={align}>
        <div className="max-h-48 overflow-y-auto p-1">
          <div className="flex flex-col gap-0.5">
            {allowExclude && includeExcludeHint && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">{includeExcludeHint}</div>
            )}
            {hasActiveSelection && (
              <button
                type="button"
                onClick={clearAll}
                className="w-full text-left px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground rounded"
              >
                {clearAllLabel}
              </button>
            )}
            {canCreateOption && createLabel && (
              <button
                type="button"
                onClick={createFromSearch}
                className="w-full text-left px-2 py-1.5 text-sm hover:bg-accent hover:text-foreground rounded"
              >
                {createLabel(normalizedSearch)}
              </button>
            )}
            {filteredOptions.length === 0 && !canCreateOption ? (
              <div className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
            ) : (
              filteredOptions.map((opt) => {
                const polarity = getFilterOptionPolarity(opt.value, [...selected], [...activeExcluded]);
                const isIncluded = polarity === 'include';
                const isExcluded = polarity === 'exclude';
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleToggle(opt.value)}
                    className="flex w-full items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-accent text-sm text-left"
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border border-foreground/50',
                        isIncluded && 'border-foreground bg-foreground text-background',
                        isExcluded && 'border-destructive bg-destructive text-destructive-foreground'
                      )}
                      aria-hidden
                    >
                      {isIncluded && <Check className="h-3 w-3" strokeWidth={2.5} />}
                      {isExcluded && <Minus className="h-3 w-3" strokeWidth={2.5} />}
                    </span>
                    {opt.swatchColor ? (
                      <span
                        className="h-3 w-3 shrink-0 rounded-sm border border-border"
                        style={{ backgroundColor: opt.swatchColor }}
                        aria-hidden
                      />
                    ) : null}
                    <span className={cn(isExcluded && 'text-muted-foreground line-through')}>
                      {opt.label}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
